import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { PageFlip } from "page-flip";
import { fileUrl } from "@/lib/api";
import { PageView } from "@/components/player/PageView";

// Realistic page-curl book (StPageFlip). Page DOM nodes are owned by PageFlip; React renders into them via portals.
export const FlipBook = forwardRef(({ pages, layers, linesMap, hl, current, size, spread, lowBandwidth, onFlip, onFlipStart, onAction, playingAudio, onFirstPaint }, ref) => {
  const host = useRef();
  const pf = useRef(null);
  const cb = useRef(onFlip);
  const startCb = useRef(onFlipStart);
  const startRef = useRef(current);
  const [nodes, setNodes] = useState([]);
  cb.current = onFlip;
  startCb.current = onFlipStart;
  startRef.current = current;

  // StPageFlip's animated backward flip is a no-op in forced-portrait mode, so in single-page
  // mode backward/jumps use the library's instant turnToPage and we notify the player explicitly
  // (turnToPage does not always emit the "flip" event the player listens to).
  useImperativeHandle(ref, () => {
    const b = () => pf.current;
    const ci = () => b()?.getCurrentPageIndex?.() ?? startRef.current;
    const last = () => (b()?.getPageCount?.() ?? pages.length) - 1;
    const jump = (t) => { const n = Math.max(0, Math.min(last(), t)); if (b()) { b().turnToPage(n); cb.current?.(n); } };
    return {
      next: () => (spread ? b()?.flipNext("bottom") : jump(ci() + 1)),
      prev: () => (spread ? b()?.flipPrev("bottom") : jump(ci() - 1)),
      flip: (i) => (spread ? b()?.flip(i, "bottom") : jump(i)),
    };
  });

  useEffect(() => {
    if (!size.w) return;
    const els = pages.map(() => {
      const d = document.createElement("div");
      d.className = "bg-white overflow-hidden";
      return d;
    });
    const mount = document.createElement("div");
    host.current.appendChild(mount);
    const book = new PageFlip(mount, {
      width: size.w, height: size.h, size: "stretch",
      minWidth: spread ? Math.round(size.w * 0.4) : size.w, maxWidth: size.w, minHeight: 100, maxHeight: size.h,
      autoSize: true, usePortrait: true, showCover: spread, drawShadow: true, maxShadowOpacity: 0.4,
      flippingTime: 650, startPage: startRef.current, mobileScrollSupport: false,
      disableFlipByClick: true, clickEventForward: true, showPageCorners: true, swipeDistance: 30,
    });
    book.loadFromHTML(els);
    book.on("flip", (e) => cb.current(e.data));
    book.on("changeState", (e) => e.data === "flipping" && startCb.current?.());
    pf.current = book;
    setNodes(els);
    // Touch: complete the turn on a horizontal swipe in either direction.
    let t0 = null;
    const onStart = (e) => { const t = e.changedTouches[0]; t0 = t && { x: t.clientX, y: t.clientY }; };
    const onEnd = (e) => {
      const t = e.changedTouches[0];
      if (!t0 || !t) return;
      const dx = t.clientX - t0.x, dy = t.clientY - t0.y;
      t0 = null;
      if (Math.abs(dx) < size.w * 0.18 || Math.abs(dx) < Math.abs(dy)) return;
      e.stopPropagation();
      const block = mount.querySelector(".stf__block");
      if (block && book.getState() === "user_fold") {
        const r = block.getBoundingClientRect();
        const pos = { x: dx < 0 ? -40 : r.width + 40, y: t.clientY - r.top };
        book.userMove(pos, true);
        book.userStop(pos);
        return;
      }
      // Library didn't start a fold (common for backward swipes in portrait) → navigate directly.
      if (spread) { if (dx < 0) book.flipNext("bottom"); else book.flipPrev("bottom"); }
      else {
        const ci = book.getCurrentPageIndex?.() ?? startRef.current;
        const n = Math.max(0, Math.min((book.getPageCount?.() ?? pages.length) - 1, ci + (dx < 0 ? 1 : -1)));
        book.turnToPage(n); cb.current?.(n);
      }
    };
    mount.addEventListener("touchstart", onStart, { passive: true });
    mount.addEventListener("touchend", onEnd);
    return () => {
      mount.removeEventListener("touchstart", onStart);
      mount.removeEventListener("touchend", onEnd);
      pf.current = null;
      setNodes([]);
      book.destroy();
    };
  }, [pages, size.w, size.h, spread]);

  const visible = (i) => i === current || (spread && current > 0 && i === current + 1);
  const near = (i) => i >= current - 2 && i <= current + 3;

  return (
    <div style={{ width: spread ? size.w * 2 : size.w }} className="relative" data-testid={spread ? "flipbook-spread" : "flipbook-single"}>
      <div ref={host} />
      {spread && <div className="pointer-events-none absolute inset-0 z-[60] book-spine" />}
      {nodes.map((el, i) => createPortal(
        near(i) ? (
          <PageView page={pages[i]} layers={layers[pages[i].index]} lines={linesMap?.[pages[i].index]} highlight={hl?.page === pages[i].index ? hl.lines : null} size={size} lowBandwidth={lowBandwidth} active={visible(i)} flat
            onAction={onAction} playingAudio={playingAudio} onFirstPaint={onFirstPaint} />
        ) : Math.abs(i - current) < 8 ? (
          <img src={fileUrl(pages[i].thumb)} alt="" className="h-full w-full" draggable={false} />
        ) : null,
        el, `p${i}`,
      ))}
    </div>
  );
});
